import { useState, useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { userService, UserProfile } from '@/services/userService';
import { supabase } from '@/integrations/supabase/client';

export const useProfileData = () => {
  const { user: authUser, loading: authLoading } = useSupabaseAuth();
  const [data, setData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    console.debug('useProfileData: Starting fetch');
    setLoading(true);
    setError(null);

    if (!authUser) {
      console.debug('useProfileData: No authenticated user');
      setError('Not authenticated');
      setData(null);
      setLoading(false);
      return;
    }

    console.debug('useProfileData: Auth user:', authUser.id);

    try {
      // Fetch base profile
      const baseProfile = await userService.getProfileByUserId(authUser.id);
      
      if (!baseProfile) {
        console.warn('useProfileData: No profile found for user:', authUser.id);
        setError('Profile not found');
        setData(null);
        setLoading(false);
        return;
      }

      console.debug('useProfileData: Base profile loaded:', baseProfile.account_type);

      let merged = { ...baseProfile };

      // Attempt role-specific fallback fetch if data is missing
      try {
        if (baseProfile.account_type === 'brand' && !baseProfile.brand) {
          console.debug('useProfileData: Fetching brand fallback');
          const { data: brandData, error: brandError } = await supabase
            .from('brands')
            .select('*')
            .eq('profile_id', baseProfile.id)
            .maybeSingle();
          
          if (brandData) {
            console.debug('useProfileData: Brand data fetched via fallback');
            merged.brand = brandData;
          } else if (brandError) {
            console.warn('useProfileData: Brand fallback error:', brandError);
          }
        } else if (baseProfile.account_type === 'agent' && !baseProfile.agent) {
          console.debug('useProfileData: Fetching agent fallback');
          const { data: agentData, error: agentError } = await supabase
            .from('agents')
            .select('*')
            .eq('profile_id', baseProfile.id)
            .maybeSingle();
          
          if (agentData) {
            console.debug('useProfileData: Agent data fetched via fallback');
            merged.agent = agentData;
          } else if (agentError) {
            console.warn('useProfileData: Agent fallback error:', agentError);
          }
        } else if (baseProfile.account_type === 'venture' && !baseProfile.venture_capital) {
          console.debug('useProfileData: Fetching venture fallback');
          const { data: ventureData, error: ventureError } = await supabase
            .from('venture_capitals')
            .select('*')
            .eq('profile_id', baseProfile.id)
            .maybeSingle();
          
          if (ventureData) {
            console.debug('useProfileData: Venture data fetched via fallback');
            merged.venture_capital = ventureData;
          } else if (ventureError) {
            console.warn('useProfileData: Venture fallback error:', ventureError);
          }
        } else if (baseProfile.account_type === 'talent' && !baseProfile.talent) {
          console.debug('useProfileData: Fetching talent fallback');
          const { data: talentData, error: talentError } = await supabase
            .from('talents')
            .select('*')
            .eq('profile_id', baseProfile.id)
            .maybeSingle();
          
          if (talentData) {
            console.debug('useProfileData: Talent data fetched via fallback');
            merged.talent = talentData;
          } else if (talentError) {
            console.warn('useProfileData: Talent fallback error:', talentError);
          }
        }
      } catch (fallbackError) {
        console.warn('useProfileData: Role-specific fetch failed (non-fatal):', fallbackError);
      }

      console.debug('useProfileData: Final merged data:', merged);
      setData(merged);
      setLoading(false);
    } catch (err) {
      console.error('useProfileData: Fatal error:', err);
      setError('Failed to load profile');
      setData(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchProfile();
    }
  }, [authUser?.id, authLoading]);

  const refetch = () => {
    fetchProfile();
  };

  return { data, loading, error, refetch };
};
